/**
 * cn — class name utility
 * Merges Tailwind classes, handling conflicts with clsx + tailwind-merge.
 * Install: npm install clsx tailwind-merge
 */

import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * Reed's type scale, as tailwind-merge validators.
 *
 * WHY THIS EXISTS. Stock tailwind-merge only recognises `text-*` as a font size
 * when the value looks like a t-shirt size (`text-sm`) or an arbitrary length
 * (`text-[12px]`). Every other `text-*` falls through to its `text-color`
 * group. Reed's scale is named `text-body-sm-m`, `text-h3`, `text-overline` —
 * none of which look like t-shirt sizes — so tailwind-merge classified them as
 * colours and then dropped whichever came first when a real colour followed:
 *
 *   cn('text-body-sm-m', 'text-brand-piccadilly-blue')
 *     → 'text-brand-piccadilly-blue'          // size silently gone
 *   cn('text-text-primary', 'text-body-xs-m')
 *     → 'text-body-xs-m'                      // colour silently gone
 *
 * Both shipped: AttributePill rendered at the inherited font size, and
 * StatusPill at the inherited colour. The failure is invisible in review — the
 * source reads correctly and only the built class string is wrong — so this is
 * pinned by cn.test.ts rather than left to inspection.
 *
 * Matched by shape, not by an enumerated list, so a new token in the existing
 * families is covered the day it lands. Keep in step with `fontSize` in
 * tailwind.config.js; nothing under `colors` matches these patterns, and adding
 * a colour that did would reintroduce the bug in the other direction.
 */
const FONT_SIZE_PATTERN = new RegExp(
  '^(?:' +
    // big-title-xl-desktop … big-title-l-mobile
    'big-title-(?:xl|l)-(?:desktop|mobile)' +
    // h1 … h6, h1-mobile … h6-mobile
    '|h[1-6](?:-mobile)?' +
    // h1-book, h1-book-mobile
    '|h1-book(?:-mobile)?' +
    // h1-secondary … h3-secondary, and their -mobile pairs
    '|h[1-3]-secondary(?:-mobile)?' +
    // body-xs … body-xl, optional -m (mobile), optional weight suffix
    '|body-(?:xs|sm|md|lg|xl)(?:-m)?(?:-semibold|-bold|-link)?' +
    // overline, overline-m, and their -bold pairs
    '|overline(?:-m)?(?:-bold)?' +
  ')$',
);

const isReedFontSize = (value: string): boolean => FONT_SIZE_PATTERN.test(value);

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: [isReedFontSize] }],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
